import React, { useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import styles from './ImageUploader.module.scss';

interface IImageUploaderProps {
  label: string;
  value: string;
  onChange: (imageUrl: string) => void;
  acceptedFileTypes?: string;
  maxWidth?: number;
  maxHeight?: number;
  placeholder?: string;
}

export const ImageUploader: React.FC<IImageUploaderProps> = ({
  label,
  value,
  onChange,
  acceptedFileTypes = 'image/*',
  maxWidth = 200,
  maxHeight = 200,
  placeholder = 'Enter image URL or upload file'
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch('/api/upload/cloudflare', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      const result = await response.json();
      if (result.success) {
        onChange(result.imageUrl);
        // Clear the file input
        e.target.value = '';
      } else {
        setError('Upload failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
    setError(null);
  };

  const handleUrlChange = (url: string) => {
    onChange(url);
    setError(null);
  };

  return (
    <div className={`${styles.imageUploader} image-uploader`} data-testid="image-uploader">
      <label className={`${styles.label} image-uploader-label`}>{label}</label>
      
      {/* URL Input */}
      <div className="image-uploader-url-section" data-testid="image-uploader-url-input">
        <Input
          label=""
          value={value}
          onChange={handleUrlChange}
          placeholder={placeholder}
        />
      </div>
      
      {/* File Upload */}
      <div className={`${styles.uploadSection} image-uploader-file-section`} data-testid="image-uploader-file-section">
        <input
          type="file"
          accept={acceptedFileTypes}
          onChange={handleFileUpload}
          disabled={uploading}
          className={`${styles.fileInput} image-uploader-file-input`}
          id={`file-${label.replace(/\s+/g, '-').toLowerCase()}`}
          data-testid="image-uploader-file-input"
        />
        <label 
          htmlFor={`file-${label.replace(/\s+/g, '-').toLowerCase()}`}
          className={`${styles.fileLabel} image-uploader-file-label`}
          data-testid="image-uploader-file-label"
        >
          {uploading ? 'Uploading...' : 'Choose File'}
        </label>
        <span className={`${styles.uploadNote} image-uploader-note`}>or upload to Cloudflare Images</span>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`${styles.error} image-uploader-error`} data-testid="image-uploader-error">
          {error}
        </div>
      )}

      {/* Upload Status */}
      {uploading && (
        <div className="image-uploader-uploading" data-testid="image-uploader-uploading">
          Uploading image...
        </div>
      )}

      {/* Image Preview */}
      {value && (
        <div className={`${styles.preview} image-uploader-preview`} data-testid="image-uploader-preview">
          <img 
            src={value} 
            alt="Preview" 
            className="image-uploader-preview-img"
            data-testid="image-uploader-preview-img"
            style={{
              maxWidth: `${maxWidth}px`, 
              maxHeight: `${maxHeight}px`,
              objectFit: 'contain'
            }}
            onError={() => setError('Failed to load image from URL')}
            onLoad={() => setError(null)}
          />
          <Button 
            caption="Remove" 
            onClick={handleRemove}
            color="noBorder"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};